import React from 'react';
import { Link } from 'react-router-dom';
import BannerSectionStyle3 from '../Section/BannerSection/BannerSectionStyle3';
import BannerSectionStyle4 from '../Section/BannerSection/BannerSectionStyle4';
import { pageTitle } from '../../helpers/PageTitle';

const services = [
  {
    number: '01',
    title: 'Psychotherapy',
    description:
      'We provide confidential, client-centered psychotherapy tailored to individual needs. Our approach integrates evidence-based techniques to help clients understand their thoughts and emotions, build resilience, and navigate life\'s challenges with greater clarity and balance.',
    iconUrl: '/images/new/Mental health icon 21 Psychology talk.png',
    accentClass: 'from-crimson/10 to-cream-dark',
  },
  {
    number: '02',
    title: 'Training and Consultation',
    description:
      'We offer specialized training and expert consultation services for organizations, schools, and teams. Our goal is to equip stakeholders with practical tools, psychological insights, and strategies to promote mental well-being, improve performance, and foster healthier environments.',
    iconUrl: '/images/new/Mental health icon 09 Growth mind.png',
    accentClass: 'from-gold/10 to-cream-dark',
  },
  {
    number: '03',
    title: 'Workshops, Webinars & Seminars',
    description:
      'We design and deliver engaging, interactive sessions on a range of mental health and personal development topics. These programs are tailored to diverse audiences and focus on building awareness, enhancing emotional intelligence, and developing practical life skills.',
    iconUrl: '/images/new/Mental health icon 24 Calm mind.png',
    accentClass: 'from-crimson/10 to-cream-dark',
  },
  {
    number: '04',
    title: 'Corporate Social Responsibility (CSR)',
    description:
      'Through our mental health advocacy initiatives, we are committed to increasing awareness and access to psychological support. We are continuously expanding this impact and welcome partnerships with organizations looking to implement meaningful mental health CSR initiatives that drive lasting, positive change in communities.',
    iconUrl: '/images/new/Mental health icon 16 Full heart.png',
    accentClass: 'from-gold/10 to-cream-dark',
  },
];

const whyChooseUs = [
  {
    icon: '/images/icons/professional.svg',
    title: 'Culturally Sensitive',
    body: 'We provide multicultural counseling that honours your background, values, and lived experience.',
  },
  {
    icon: '/images/icons/experience.svg',
    title: 'Evidence-Based',
    body: 'Every intervention is grounded in clinical research and proven psychological frameworks.',
  },
  {
    icon: '/images/icons/comprehensive.svg',
    title: 'Tailored to You',
    body: 'No two journeys are the same. We design services around individual and organisational goals.',
  },
  {
    icon: '/images/icons/facilities.svg',
    title: 'In-Person & Virtual',
    body: 'Access support however works best for you — from our Abuja office or online from anywhere.',
  },
];

export default function OurServices() {
  pageTitle('Our Services');

  return (
    <div className="bg-cream min-h-screen">
      {/* ─── Hero Banner ─── */}
      <BannerSectionStyle3
        bgUrl="/images/about/banner_bg.svg"
        imgUrl="/images/new/Mental health icon 19 Psychology.png"
        title={"Our Services"}
        subTitle="Comprehensive psychological services designed for individuals, teams, schools, and communities — delivered with empathy and expertise."
        btnText="Book an Appointment"
        btnUrl="/appointments"
        btnText2="Meet Our Team"
        btnUrl2="/about"
      />

      {/* ─── Services Grid ─── */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-16">

          {/* Section heading */}
          <div className="text-center mb-16 animate-fade-up">
            <div className="inline-flex items-center gap-[0.6rem] text-[0.72rem] tracking-[0.2em] uppercase text-gold font-medium mb-4">
              <span className="w-[24px] h-px bg-gold block" />
              What We Offer
              <span className="w-[24px] h-px bg-gold block" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
              Our <em>Services</em>
            </h2>
            <p className="text-base text-mid font-light max-w-[560px] mx-auto mt-4 leading-relaxed">
              We offer a full spectrum of mental health and educational services — each crafted to meet you exactly where you are.
            </p>
          </div>

          {/* Service cards — alternating layout */}
          <div className="space-y-12">
            {services.map((service, index) => (
              <div
                key={service.number}
                className={`group relative flex flex-col ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } gap-0 rounded-xl overflow-hidden border border-[rgba(139,26,26,0.08)] bg-white shadow-sm hover:shadow-xl transition-all duration-700 hover:-translate-y-2 animate-fade-up`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Image / Icon panel */}
                <div
                  className={`relative flex-shrink-0 w-full md:w-[380px] min-h-[280px] md:min-h-auto bg-gradient-to-br ${service.accentClass} flex items-center justify-center p-12 transition-all duration-700 group-hover:scale-[1.02]`}
                >
                  {/* Number watermark */}
                  <span className="absolute top-6 right-8 font-serif text-[6rem] leading-none text-charcoal/5 font-bold select-none transition-all duration-700 group-hover:scale-110 group-hover:text-charcoal/[0.08]">
                    {service.number}
                  </span>

                  <img
                    src={service.iconUrl}
                    alt={service.title}
                    className="w-44 h-44 object-contain drop-shadow-2xl transition-transform duration-700 group-hover:scale-110 group-hover:rotate-3"
                  />
                </div>

                {/* Text content */}
                <div className="flex flex-col justify-center p-10 md:p-16 flex-1 bg-white">
                  {/* Eyebrow */}
                  <div className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.2em] uppercase text-crimson font-medium mb-4">
                    <span className="w-6 h-px bg-crimson block" />
                    Service {service.number}
                  </div>

                  <h3 className="font-serif text-3xl md:text-4xl font-light text-charcoal mb-6 leading-tight">
                    {service.title}
                  </h3>

                  <p className="text-[1.05rem] text-mid font-light leading-relaxed max-w-[580px] mb-8">
                    {service.description}
                  </p>

                  <div className="mt-auto">
                    <Link
                      to="/appointments"
                      className="inline-flex items-center gap-3 text-crimson text-[0.85rem] font-medium tracking-[0.12em] uppercase border-b border-crimson pb-1 transition-all duration-300 hover:gap-5 hover:text-crimson-dark hover:border-crimson-dark"
                    >
                      Book This Service <span className="text-xl">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Choose Us ─── */}
      <section className="py-24 bg-cream-dark border-y border-[rgba(139,26,26,0.08)]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-16">
          <div className="text-center mb-14 animate-fade-up">
            <div className="inline-flex items-center gap-[0.6rem] text-[0.72rem] tracking-[0.2em] uppercase text-gold font-medium mb-4">
              <span className="w-[24px] h-px bg-gold block" />
              Our Approach
              <span className="w-[24px] h-px bg-gold block" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
              Why Choose <em>Noohra?</em>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.title}
                className="group bg-white rounded-xl p-10 border border-cream shadow-sm transition-all duration-500 hover:shadow-lg hover:-translate-y-2 flex flex-col items-start animate-fade-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-full bg-cream-dark flex items-center justify-center mb-8 border border-[rgba(139,26,26,0.1)] group-hover:bg-crimson group-hover:border-crimson transition-all duration-500">
                  <img
                    src={item.icon}
                    alt={item.title}
                    className="w-6 h-6 object-contain opacity-60 group-hover:brightness-0 group-hover:invert transition-all duration-500"
                  />
                </div>
                <h4 className="font-serif text-2xl text-charcoal mb-4 leading-snug">{item.title}</h4>
                <p className="text-base text-mid font-light leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Process / How it Works ─── */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-16">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-[0.6rem] text-[0.72rem] tracking-[0.2em] uppercase text-gold font-medium mb-4">
              <span className="w-[24px] h-px bg-gold block" />
              Getting Started
              <span className="w-[24px] h-px bg-gold block" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-charcoal leading-tight">
              How It <em>Works</em>
            </h2>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-[52px] left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-px bg-[rgba(139,26,26,0.12)]" />

            {[
              {
                step: '1',
                title: 'Reach Out',
                body: 'Contact us via our booking form, phone, or email. Tell us a little about what you\'re looking for.',
                icon: '/images/icons/call.svg',
              },
              {
                step: '2',
                title: 'Initial Consultation',
                body: 'We\'ll schedule a brief consultation to understand your needs and match you with the right service.',
                icon: '/images/icons/schedule.svg',
              },
              {
                step: '3',
                title: 'Begin Your Journey',
                body: 'Start your sessions or program, supported every step of the way by our compassionate team.',
                icon: '/images/new/Mental health icon 18 Love growth.png',
              },
            ].map((step) => (
              <div key={step.step} className="flex flex-col items-center text-center relative z-10">
                {/* Step circle */}
                <div className="w-[72px] h-[72px] rounded-full bg-white border-2 border-[rgba(139,26,26,0.15)] flex items-center justify-center mb-6 shadow-sm">
                  <img
                    src={step.icon}
                    alt={step.title}
                    className="w-8 h-8 object-contain opacity-70"
                  />
                </div>

                <div className="text-[0.7rem] tracking-[0.2em] uppercase text-crimson font-medium mb-2">
                  Step {step.step}
                </div>
                <h4 className="font-serif text-2xl text-charcoal mb-3">{step.title}</h4>
                <p className="text-sm text-mid font-light leading-relaxed max-w-[260px]">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA Banner ─── */}
      <BannerSectionStyle4
        bgUrl="images/about/banner.png"
        title="Ready to Take the First Step?"
        subTitle="Whether you're seeking therapy, planning a workshop for your organisation, or exploring a CSR partnership — we're here to help. Reach out today and let's walk this journey together."
        center
      />
    </div>
  );
}
